import { useRef, useEffect } from "react";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";

interface UseCartSummaryAutoOpenProps {
  itemQty: number;
  venueId: number | undefined;
}

interface UseCartSummaryAutoOpenReturn {
  cartSummaryRef: React.RefObject<{
    open: () => void;
    close: () => void;
  } | null>;
}

/**
 * Custom hook that manages automatic opening of cart summary when basket quantity increases.
 * This is mainly to deal with upgrades not being visible to the customer until the cart summary is opened.
 * There may be better ways to solve this problem that should be A/B tested.
 */
export const useCartSummaryAutoOpen = ({
  itemQty,
  venueId,
}: UseCartSummaryAutoOpenProps): UseCartSummaryAutoOpenReturn => {
  const cartSummaryRef = useRef<{ open: () => void; close: () => void } | null>(
    null
  );
  const cartQuantityRef = useRef(itemQty);
  const { isFlagActive } = useDjangoWaffle();

  useEffect(() => {
    if (isFlagActive("open_cart_summary_on_basket_add", venueId)) {
      // If the cart quantity has increased, open the cart summary footer.
      if (itemQty > 0 && itemQty > cartQuantityRef.current) {
        cartSummaryRef.current?.open();
        cartQuantityRef.current = itemQty;
      } else if (itemQty < cartQuantityRef.current) {
        cartQuantityRef.current = itemQty;
      }
    }
  }, [itemQty, isFlagActive, venueId]);

  return { cartSummaryRef };
};
